import { z } from 'zod';

/**
 * The "app manager" area of the customer app.
 *
 * An ordinary customer portal login carrying one extra privilege
 * (`customer_users.is_app_admin`) sees a read-only manager area: the shop's
 * orders as they arrive, and a barcode/search product lookup showing everything
 * about a product — sale prices, COST (التكلفة), stock, what has sold and what
 * was bought.
 *
 * Everything here is DISPLAY ONLY, with one exception: while the tenant keeps
 * the `manager_products` module on (Settings → Modules — like every module it
 * defaults on, and one switch or a platform lock turns it off), the manager
 * can also CREATE and EDIT catalogue products (name/barcode/price/category/
 * description/active — see ManagerProductCreateSchema below). Turning the
 * module off closes the routes immediately (403) and the app's buttons vanish
 * with the canEdit flags, so a shop that wants the old read-only blast radius
 * — "someone saw my margins", never "someone edited my catalogue" — is one
 * toggle away from it.
 */

/** Money in one currency — mirrors the reporting shape used elsewhere. */
export interface ManagerMoney {
  currencyCode: string;
  amount: string;
}

/**
 * Where a sale came from.
 *
 * These are the values `sales_orders.channel` actually carries, plus the two
 * the column cannot hold: 'all' (no filter) and 'manual', which is how a NULL
 * channel — an order keyed in from the back office — is named to a human.
 * A shop that never opened its storefront has every sale under 'pos', so
 * filtering the manager area to 'online' shows such an owner an empty app.
 */
export const MANAGER_CHANNELS = ['all', 'pos', 'online', 'field', 'manual'] as const;
export type ManagerChannel = (typeof MANAGER_CHANNELS)[number];

/** Today's trade through one channel — never 'all', which is the sum of these. */
export interface ManagerChannelToday {
  channel: Exclude<ManagerChannel, 'all'>;
  orders: number;
  sales: ManagerMoney[];
}

/** One order in the manager's order list. */
export interface ManagerOrderSummary {
  id: string;
  docNumber: string;
  docDate: string;
  status: string;
  /** Who placed it. Null for a walk-in/anonymous order. */
  customerName: string | null;
  customerPhone: string | null;
  total: string;
  currencyCode: string;
  lineCount: number;
  /** Where it came from — 'online' for a storefront/app order. */
  channel: string | null;
  createdAt: string;
}

export interface ManagerOrderLine {
  itemId: string | null;
  description: string;
  quantity: string;
  unitPrice: string;
  lineTotal: string;
}

export interface ManagerOrderDetail extends ManagerOrderSummary {
  lines: ManagerOrderLine[];
  /** Delivery/contact details captured at checkout, when present. */
  address: string | null;
  note: string | null;
}

/** A search hit — deliberately thin, the detail call carries the weight. */
export interface ManagerProductHit {
  id: string;
  name: string;
  code: string;
  barcode: string | null;
  imageUrl: string | null;
  salePrice: string | null;
  currencyCode: string | null;
  /** Total on hand across warehouses; null when the item isn't tracked. */
  onHand: string | null;
  /**
   * WHAT THE SCAN ACTUALLY HIT — set only by the barcode lookup.
   *
   * A code can belong to the product itself, to one of its packaging units, to
   * an alias label (which may itself be pinned to a unit) or to a variant. The
   * product is the same either way, but "which one did I just scan" is the whole
   * question when a carton and a piece sit in the same hand, so the resolver
   * says so instead of throwing the answer away.
   */
  matched?: ManagerScanMatch | null;
}

/** Which sellable thing a scanned code turned out to name. */
export interface ManagerScanMatch {
  /** The code as the resolver matched it (after check-digit fallbacks). */
  barcode: string;
  kind: 'item' | 'alias' | 'unit' | 'variant';
  /** The packaging unit the code names, when it names one. */
  unitId: string | null;
  unitName: string | null;
  unitFactor: string | null;
  /** The option/shade the code names, when it names one. */
  variantId: string | null;
  variantName: string | null;
}

/** One sellable unit (piece / pack / carton) with its own absolute price. */
export interface ManagerProductUnit {
  id: string;
  name: string;
  factor: string;
  price: string | null;
  barcode: string | null;
  /** A retired unit is still listed, and plainly marked — it explains old lines. */
  isActive: boolean;
  /**
   * What ONE of these costs: the item's weighted average cost × this unit's
   * factor. Cost is only ever held per base unit, so a carton's cost is derived,
   * never stored — and it is null exactly when the average cost is.
   */
  cost: string | null;
  costCurrencyCode: string | null;
  /** The catalogue's own buy price for this unit, when the shop set one. */
  purchasePrice: string | null;
}

/**
 * A choice a shopper makes on the product page — a shade, a size — with its own
 * price and barcode. One product, several sellable options; not a packaging
 * unit (that is ManagerProductUnit) and not a separate product.
 */
export interface ManagerProductVariant {
  id: string;
  name: string;
  /** Arabic label for the Arabic storefront; null falls back to `name`. */
  nameAr: string | null;
  price: string | null;
  barcode: string | null;
}

/** Stock in one warehouse, with the cost basis held there. */
export interface ManagerStockRow {
  warehouseId: string;
  warehouseName: string;
  quantity: string;
  avgCost: string | null;
  avgCostCurrencyCode: string | null;
}

/** A past purchase of this product — what it cost and from whom. */
export interface ManagerPurchaseRow {
  docId: string;
  docNumber: string;
  docDate: string;
  supplierName: string | null;
  quantity: string;
  /**
   * The unit the line was BOUGHT IN, and how many base units it holds. Without
   * these, "0.46" sits under a per-piece cost of "0.05" looking like a
   * contradiction — when it is simply the price of a packet of ten.
   */
  unitName: string | null;
  unitFactor: string | null;
  unitPrice: string;
  lineTotal: string;
  currencyCode: string;
  status: string;
}

/** A past sale of this product, from either channel. */
export interface ManagerSaleRow {
  source: 'pos' | 'online';
  docId: string;
  docNumber: string;
  docDate: string;
  customerName: string | null;
  quantity: string;
  /** The unit this line was SOLD in — see ManagerPurchaseRow.unitName. */
  unitName: string | null;
  unitFactor: string | null;
  unitPrice: string;
  lineTotal: string;
  currencyCode: string;
}

/**
 * Everything about one product, for the manager's product screen.
 *
 * `cost` is the weighted average cost across warehouses — the number the owner
 * actually means by "التكلفة". `margin` is computed server-side rather than in
 * the app so every surface agrees on what it means (sale price less average
 * cost, in the cost's own currency, and null when the two differ because nothing
 * here is ever converted).
 */
export interface ManagerProductDetail {
  id: string;
  name: string;
  code: string;
  description: string | null;
  barcode: string | null;
  /** Alias barcodes plus each packaging unit's own code. */
  extraBarcodes: string[];
  /** JUST the alias labels (no unit codes) — what the edit form hydrates from. */
  aliasBarcodes?: string[];
  imageUrl: string | null;
  /** EVERY image of this product, oldest first — `imageUrl` is simply the head. */
  imageUrls: string[];
  categoryName: string | null;
  /** The raw id too — the edit form needs to preselect, not just display. */
  categoryId: string | null;
  /** EXTRA category memberships (multi_category module); primary excluded. */
  extraCategoryIds?: string[];
  brandName: string | null;
  brandId: string | null;
  /** The catalogue's default buy price — the edit form's cost field. */
  purchasePrice: string | null;
  /** The currency that buy price is in; the item's own, not the shop's. */
  purchaseCurrencyCode?: string | null;
  /** The storefront Featured flag, editable from the manager form. */
  isFeatured: boolean;
  unitOfMeasure: string | null;
  isActive: boolean;
  isTracked: boolean;
  /** Hidden from the online store (still sellable at the till). */
  isHidden?: boolean;
  /** Listed on the store and the app ("نشر") — the store_products overlay. */
  isPublished?: boolean;
  /** YouTube link shown on the storefront product page. */
  videoUrl?: string | null;
  /** True when the `manager_products` module is on — shows the Edit button. */
  canEdit?: boolean;

  salePrice: string | null;
  saleCurrencyCode: string | null;
  /** Store-specific override, when the product is published with one. */
  storePrice: string | null;
  units: ManagerProductUnit[];
  /** Options/shades (variants module). Empty when the product has none. */
  variants: ManagerProductVariant[];
  /** True when the `variants` module is on — the form hides the section when off. */
  variantsEnabled?: boolean;
  /** Per customer-group prices, when the pricing module is in use. */
  priceLists: Array<{ id: string; name: string; unitPrice: string; currencyCode: string | null }>;

  /** Weighted average cost across warehouses; null when never purchased. */
  avgCost: string | null;
  avgCostCurrencyCode: string | null;
  /**
   * What one BASE unit costs the shop — the number every unit price is derived
   * from, and the one the margin is taken against.
   *
   * The owner's rule, quoted in cost-basis.ts: "it's only one value cost, like
   * the last cost". So the newest posted bill wins, and the weighted stock
   * average is the fallback for a product that has never been bought through
   * one. Same ladder the profit report uses, so the two cannot disagree.
   */
  costBasis: string | null;
  costBasisCurrencyCode: string | null;
  /** Which rung answered — worth showing, since the two can differ a lot. */
  costBasisSource: 'last_bill' | 'stock_average' | null;
  /** Sale price less the cost basis, when both are in the same currency. */
  marginAmount: string | null;
  marginPercent: number | null;
  lastPurchasePrice: string | null;
  lastPurchaseCurrencyCode: string | null;
  lastPurchaseAt: string | null;
  /** The unit that price was per, when the bill was written in a bigger one. */
  lastPurchaseUnitName: string | null;
  lastPurchaseUnitFactor: string | null;
  /** The same purchase expressed per BASE unit — comparable with `avgCost`. */
  lastPurchasePerBase: string | null;

  onHand: string | null;
  stock: ManagerStockRow[];

  /** Lifetime sales, same basis as the staff item report (POS + online). */
  soldUnits: string;
  salesCount: number;
  revenue: ManagerMoney[];
  views: number;
  firstSoldAt: string | null;
  lastSoldAt: string | null;
  /** Units sold per calendar month, chronological — drives the little trend bars. */
  monthly: Array<{ month: string; units: string }>;

  recentSales: ManagerSaleRow[];
  recentPurchases: ManagerPurchaseRow[];
}

/**
 * The window the manager's home is showing.
 *
 * Calendar days, not rolling hours: '7d' is today plus the six days before it,
 * because an owner comparing weeks means weeks, and a 168-hour window that
 * starts mid-afternoon compares nothing to anything.
 */
export const MANAGER_PERIODS = ['today', '7d', '30d'] as const;
export type ManagerPeriod = (typeof MANAGER_PERIODS)[number];

/** One day of trade — the sparkline's raw material. */
export interface ManagerDailyPoint {
  /** Calendar date, YYYY-MM-DD, in the server's own timezone. */
  date: string;
  orders: number;
  sales: ManagerMoney[];
}

/** What sold most in the window. */
export interface ManagerTopProduct {
  itemId: string;
  name: string;
  code: string;
  imageUrl: string | null;
  units: string;
  revenue: ManagerMoney[];
}

/**
 * Everything the manager's home screen shows, in one round trip.
 *
 * Same principle as the product screen: a phone on a shop's wifi should make
 * one request, not eight. `previous` is the identical window immediately
 * before this one, so the app can render a change without a second call and
 * without inventing its own idea of "last week".
 */
export interface ManagerSummary {
  period: ManagerPeriod;
  /** Window bounds, ISO. `to` is the moment the summary was taken. */
  from: string;
  to: string;

  orders: number;
  sales: ManagerMoney[];
  channels: ManagerChannelToday[];
  previous: { from: string; to: string; orders: number; sales: ManagerMoney[] };
  daily: ManagerDailyPoint[];
  topProducts: ManagerTopProduct[];

  /** The newest few, so the home screen is worth reading on its own. */
  recentOrders: ManagerOrderSummary[];
  /** The emptiest few — the rest are behind the restock screen. */
  restock: ManagerProductHit[];

  outOfStockCount: number;
  lowStockCount: number;
  productCount: number;
  lowStockThreshold: number;
  /** Online orders still in draft — the only thing here that asks for an action. */
  ordersPending: number;
  /** True when the `manager_products` module is on — shows the add/edit UI. */
  canEditProducts?: boolean;
}

export const ManagerSummaryQuerySchema = z.object({
  period: z.enum(MANAGER_PERIODS).optional(),
});
export type ManagerSummaryQuery = z.infer<typeof ManagerSummaryQuerySchema>;

/** Headline numbers for the manager's home screen. */
export interface ManagerOverview {
  /** Orders today through EVERY channel — the till included. */
  ordersToday: number;
  /**
   * Online orders still sitting in draft. Deliberately online-only: a draft POS
   * order is a parked sale the cashier is holding, not something the owner is
   * being asked to action.
   */
  ordersPending: number;
  /** Today's order value across every channel, per currency. */
  salesToday: ManagerMoney[];
  /** The same money split by where it came from — till vs online vs field. */
  channelsToday: ManagerChannelToday[];
  /** Products at or below zero on hand — what needs reordering. */
  outOfStockCount: number;
  lowStockCount: number;
  productCount: number;
  /** The "running low" line, so the app can say ≤ N rather than guess it. */
  lowStockThreshold: number;
}

/**
 * The orders screen's chart: daily trade over a short window, through the SAME
 * channel filter the list is running — bars that quietly include a till the
 * list below them is hiding would make the two halves of one screen disagree.
 */
export interface ManagerOrdersSeries {
  /** Calendar days, chronological and gap-filled. */
  points: ManagerDailyPoint[];
  /** Window totals — exactly what the points sum to. */
  orders: number;
  sales: ManagerMoney[];
}

export const ManagerSeriesQuerySchema = z.object({
  channel: z.enum(MANAGER_CHANNELS).optional(),
  days: z.coerce.number().int().min(2).max(60).optional(),
});
export type ManagerSeriesQuery = z.infer<typeof ManagerSeriesQuerySchema>;

export const ManagerOrdersQuerySchema = z.object({
  status: z.string().max(40).optional(),
  q: z.string().max(200).optional(),
  channel: z.enum(MANAGER_CHANNELS).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});
export type ManagerOrdersQuery = z.infer<typeof ManagerOrdersQuerySchema>;

/**
 * Manager product WRITES — the one deliberate exception to this surface's
 * read-only rule, and only when the tenant switched the `manager_products`
 * module on (Settings → Modules; off = the routes 403). Kept to catalogue
 * facts a shop owner fixes from the aisle: name, barcode, price, category,
 * description, active — and the packaging units and options the product sells
 * in. Deliberately NOT here: cost (computed from purchases) and stock (that is
 * an adjustment with GL consequences, which is why an option carries no count).
 */
/** A packaging unit as the manager form edits it — pack/carton with own price. */
export const ManagerProductUnitInputSchema = z.object({
  name: z.string().min(1).max(64),
  /** How many base units this holds (12 for a pack of 12). */
  factor: z.string().regex(/^\d+(\.\d{1,4})?$/).refine((v) => Number(v) > 0),
  salePrice: z.string().regex(/^\d+(\.\d{1,4})?$/).optional(),
  barcode: z.string().max(64).optional(),
});
export type ManagerProductUnitInput = z.infer<typeof ManagerProductUnitInputSchema>;

/**
 * One option row as the manager form edits it.
 *
 * No quantity on purpose: this surface never writes stock (see the note above),
 * so an option is added with its name, its price and its barcode, and the shelf
 * count stays where stock is actually managed.
 */
export const ManagerProductVariantInputSchema = z.object({
  name: z.string().min(1).max(128),
  nameAr: z.string().max(128).optional(),
  salePrice: z.string().regex(/^\d+(\.\d{1,4})?$/).optional(),
  barcode: z.string().max(64).optional(),
});
export type ManagerProductVariantInput = z.infer<typeof ManagerProductVariantInputSchema>;

export const ManagerProductCreateSchema = z.object({
  name: z.string().min(1).max(200),
  /** product = a thing on a shelf (stock tracked); service = no shelf at all. */
  itemType: z.enum(['product', 'service']).optional(),
  barcode: z.string().max(64).optional(),
  /** EXTRA labels that also scan to this product (a second manufacturer EAN). */
  aliasBarcodes: z.array(z.string().min(1).max(64)).max(20).optional(),
  /** Positive money string; priced in the tenant's base currency server-side. */
  salePrice: z.string().regex(/^\d+(\.\d{1,4})?$/).optional(),
  /** The default BUY price (reference for purchasing) — not the computed avg cost. */
  purchasePrice: z.string().regex(/^\d+(\.\d{1,4})?$/).optional(),
  categoryId: z.string().uuid().optional(),
  /** EXTRA categories (multi_category module) — primary excluded. */
  extraCategoryIds: z.array(z.string().uuid()).max(10).optional(),
  brandId: z.string().uuid().optional(),
  description: z.string().max(2000).optional(),
  /** The base unit's own name (قطعة, دست…). Defaults server-side to 'ea'. */
  unitOfMeasure: z.string().min(1).max(16).optional(),
  /** Packaging units (pack/carton), each with its own price and barcode. */
  units: z.array(ManagerProductUnitInputSchema).max(10).optional(),
  /** Options/shades, each sellable with its own price and barcode. */
  variants: z.array(ManagerProductVariantInputSchema).max(50).optional(),
  /** The storefront's Featured section flag — same one the admin form sets. */
  isFeatured: z.boolean().optional(),
  /** Sell at the till but keep OFF the online store. */
  isHidden: z.boolean().optional(),
  /** Put it on the store and the app straight away — the form ticks this. */
  isPublished: z.boolean().optional(),
  /** YouTube link shown on the storefront product page. */
  videoUrl: z.string().max(500).optional(),
});
export type ManagerProductCreate = z.infer<typeof ManagerProductCreateSchema>;

export const ManagerProductUpdateSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  barcode: z.string().max(64).nullable().optional(),
  /** Omit to leave untouched; [] clears — the same contract as the admin form. */
  aliasBarcodes: z.array(z.string().min(1).max(64)).max(20).optional(),
  salePrice: z.string().regex(/^\d+(\.\d{1,4})?$/).nullable().optional(),
  purchasePrice: z.string().regex(/^\d+(\.\d{1,4})?$/).nullable().optional(),
  categoryId: z.string().uuid().nullable().optional(),
  /** Omit to leave untouched; [] clears. */
  extraCategoryIds: z.array(z.string().uuid()).max(10).optional(),
  brandId: z.string().uuid().nullable().optional(),
  description: z.string().max(2000).nullable().optional(),
  unitOfMeasure: z.string().min(1).max(16).optional(),
  /** Omit to leave untouched; the array sent REPLACES the item's units. */
  units: z.array(ManagerProductUnitInputSchema).max(10).optional(),
  /** Same contract as units: omit to leave untouched, the array sent REPLACES. */
  variants: z.array(ManagerProductVariantInputSchema).max(50).optional(),
  isFeatured: z.boolean().optional(),
  isHidden: z.boolean().optional(),
  isPublished: z.boolean().optional(),
  isActive: z.boolean().optional(),
  videoUrl: z.string().max(500).nullable().optional(),
});
export type ManagerProductUpdate = z.infer<typeof ManagerProductUpdateSchema>;

/** A category (or brand) as the picker shows it — flat, active ones only. */
export interface ManagerCategoryOption {
  id: string;
  name: string;
}

/**
 * Events a manager's phone can be told about (in-app inbox + device push).
 * Server-owned catalog: the app renders whatever GET /notification-prefs
 * returns, so a new event here reaches phones without an app release.
 */
export const MANAGER_NOTIFY_EVENTS = ['store.order.placed', 'pos.sale.finalized', 'payment.received', 'customer.type_requested', 'fib.order_paid'] as const;
export type ManagerNotifyEvent = (typeof MANAGER_NOTIFY_EVENTS)[number];

/** The toggle surface, per event type. Missing prefs = everything on. */
export interface ManagerNotifyPrefs {
  events: Array<{ type: ManagerNotifyEvent; enabled: boolean }>;
}

export const ManagerNotifyPrefsUpdateSchema = z.object({
  /** The ENABLED event types; anything absent from the list is off. */
  events: z.array(z.enum(MANAGER_NOTIFY_EVENTS)).max(MANAGER_NOTIFY_EVENTS.length),
});
export type ManagerNotifyPrefsUpdate = z.infer<typeof ManagerNotifyPrefsUpdateSchema>;

/**
 * The restock list. `attention` is both halves at once — everything at or below
 * the low line, worst first — because that is the one list an owner walking the
 * aisles actually wants.
 */
export const ManagerStockQuerySchema = z.object({
  mode: z.enum(['attention', 'out', 'low']).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});
export type ManagerStockQuery = z.infer<typeof ManagerStockQuerySchema>;
